/**
 * Known View Component
 * Display uploaded files (PDF/Excel) with extraction links
 * Supports customer folder organization
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Description as PdfIcon,
  TableChart as ExcelIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateFolderIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  DriveFileMove as MoveIcon,
  Rule as RuleIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBOM } from '../contexts/BOMContext';
import * as bomApi from '../services/bomApi';
import { FileListResponse, CustomerFolder } from '../types/bom.types';

const KnownView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    setUploadedPdf, 
    setUploadedXls, 
    setLoading, 
    showToast,
    clearExtractedItems,
    addExtractedItems,
    setComponents,
    setManufacturers,
    setMetadata,
    setFeatures,
    setDimensions,
    setTotalPages
  } = useBOM();

  const [fileList, setFileList] = useState<FileListResponse | null>(null);
  const [folders, setFolders] = useState<CustomerFolder[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; filename: string; type: 'pdf' | 'xls' | 'folder'; folderId?: string }>({
    open: false,
    filename: '',
    type: 'pdf'
  });
  const [fileTab, setFileTab] = useState<'pdf' | 'excel' | 'folders'>('pdf');
  
  // Folder dialogs
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDescription, setNewFolderDescription] = useState('');
  
  // Move file dialog
  const [moveFileDialog, setMoveFileDialog] = useState<{ open: boolean; filename: string; currentFolderId?: string; jobId?: string }>({
    open: false,
    filename: '',
  });
  const [targetFolderId, setTargetFolderId] = useState<string>('');
  
  // Expanded folders
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['unassigned']));
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  const loadFileList = async () => {
    try {
      const data = await bomApi.listFiles();
      setFileList(data);
    } catch (error: any) {
      showToast('error', `Failed to load file list: ${error.message}`);
    }
  };

  const loadFolders = async () => {
    try {
      const data = await bomApi.listFolders();
      setFolders(data);
    } catch (error: any) {
      showToast('error', `Failed to load folders: ${error.message}`);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      showToast('error', 'Customer name is required');
      return;
    }
    
    try {
      setLoading(true, 'Creating folder...');
      await bomApi.createFolder(newFolderName.trim(), newFolderDescription.trim());
      showToast('success', `Folder created for customer: ${newFolderName}`);
      setCreateFolderDialog(false);
      setNewFolderName('');
      setNewFolderDescription('');
      await loadFolders();
    } catch (error: any) {
      showToast('error', `Failed to create folder: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    try {
      setLoading(true, 'Deleting folder...');
      await bomApi.deleteFolder(folderId);
      showToast('success', 'Folder deleted');
      setDeleteDialog({ open: false, filename: '', type: 'pdf' });
      await loadFolders();
    } catch (error: any) {
      showToast('error', `Failed to delete folder: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveFile = async () => {
    if (!moveFileDialog.filename) return;
    
    try {
      setLoading(true, 'Moving file...');
      
      // Get file from file list
      const file = fileList?.pdf_files.find(f => f.filename === moveFileDialog.filename);
      if (!file) {
        throw new Error('File not found');
      }
      
      // Get job ID from dialog state first, then fall back to file properties
      const jobId = moveFileDialog.jobId || (file as any).job_id || file.extractions?.[0]?.job_id;
      if (!jobId) {
        // If no job ID, we need to extract the file first
        showToast('info', 'Please extract this file first before assigning to a folder');
        setMoveFileDialog({ open: false, filename: '' });
        return;
      }
      
      if (targetFolderId) {
        await bomApi.assignFilesToFolder(targetFolderId, [jobId]);
        showToast('success', `File moved to folder`);
      } else {
        // Move to unassigned
        if (moveFileDialog.currentFolderId) {
          await bomApi.removeFilesFromFolder(moveFileDialog.currentFolderId, [jobId]);
          showToast('success', `File moved to unassigned`);
        }
      }
      
      setMoveFileDialog({ open: false, filename: '' });
      setTargetFolderId('');
      await loadFileList();
      await loadFolders();
    } catch (error: any) {
      showToast('error', `Failed to move file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (filename: string, fileType: 'pdf' | 'xls') => {
    try {
      setLoading(true, 'Deleting file...');
      const response = await fetch(`/api/uploads/${filename}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete file');
      }
      
      showToast('success', `File deleted: ${filename}`);
      setDeleteDialog({ open: false, filename: '', type: 'pdf' });
      
      await loadFileList();
    } catch (error: any) {
      showToast('error', `Failed to delete file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Load file list and folders on mount
  useEffect(() => {
    loadFileList();
    loadFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLoadFile = async (file: any, fileType: 'pdf' | 'xls') => {
    setLoading(true);
    try {
      if (fileType === 'pdf') {
        setUploadedPdf(file.filename, file.url);
        
        if (file.extractions && file.extractions.length > 0) {
          const sortedExtractions = [...file.extractions].sort((a, b) => 
            new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime()
          );
          const mostRecentExtraction = sortedExtractions[0];
          
          setLoading(true, 'Loading extraction...');
          const loadedData = await bomApi.loadExtraction(mostRecentExtraction.job_id);
          
          if (loadedData.xls_filename && loadedData.xls_url) {
            setUploadedXls(loadedData.xls_filename, loadedData.xls_url);
          }
          
          clearExtractedItems();
          addExtractedItems(loadedData.items);
          setComponents(loadedData.components || []);
          setManufacturers(loadedData.manufacturers || []);
          setMetadata(loadedData.metadata || null);
          setFeatures(loadedData.features || []);
          setDimensions(loadedData.dimensions || []);
          setTotalPages(loadedData.total_pages || 1);
          
          showToast('success', `Loaded PDF and extraction with ${loadedData.items.length} items`);
        } else {
          showToast('success', 'PDF loaded. No extractions available.');
        }
      } else {
        setUploadedXls(file.filename, file.url);
        showToast('success', 'Excel file loaded.');
      }
      
      // Navigate to training with folder context if file has a folder assignment
      const navigationState = file.folder_id ? { folderId: file.folder_id } : undefined;
      navigate('/training', { state: navigationState });
    } catch (error: any) {
      showToast('error', `Failed to load file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadExtraction = async (jobId: string, pdfFilename: string, xlsFilename?: string) => {
    setLoading(true);
    try {
      const loadedData = await bomApi.loadExtraction(jobId);
      
      setUploadedPdf(pdfFilename, loadedData.pdf_url || `/uploads/${pdfFilename}`);
      if (xlsFilename && loadedData.xls_url) {
        setUploadedXls(xlsFilename, loadedData.xls_url);
      } else if (loadedData.xls_filename && loadedData.xls_url) {
        setUploadedXls(loadedData.xls_filename, loadedData.xls_url);
      }
      
      clearExtractedItems();
      addExtractedItems(loadedData.items);
      
      const manufacturerMap = new Map<string, any>();
      const componentMap = new Map<string, any>();
      
      loadedData.items.forEach((item: any) => {
        const mfr = item.manufacturer_suggestion || item.manufacturer || 'Unknown';
        if (!manufacturerMap.has(mfr)) {
          manufacturerMap.set(mfr, {
            manufacturer: mfr,
            component_count: 0,
            components: [],
            pages: [],
          });
        }
        const mfrData = manufacturerMap.get(mfr)!;
        const qty = typeof item.qty === 'string' ? parseInt(item.qty) || 1 : (item.qty || item.quantity || 1);
        mfrData.component_count += qty;
        const ref = item.reference || item.raw_line?.substring(0, 20) || 'Unknown';
        if (!mfrData.components.includes(ref)) {
          mfrData.components.push(ref);
        }
        const page = item.page || (item.pages && item.pages[0]) || 1;
        if (page && !mfrData.pages.includes(page)) {
          mfrData.pages.push(page);
        }
        
        const compType = item.component_type || item.type || 'Unknown';
        if (!componentMap.has(compType)) {
          componentMap.set(compType, {
            type: compType,
            count: 0,
            examples: [],
            pages: [],
          });
        }
        const compData = componentMap.get(compType)!;
        compData.count += qty;
        if (!compData.examples.includes(ref)) {
          compData.examples.push(ref);
        }
        if (page && !compData.pages.includes(page)) {
          compData.pages.push(page);
        }
      });
      
      setComponents(Array.from(componentMap.values()).sort((a, b) => b.count - a.count));
      setManufacturers(Array.from(manufacturerMap.values()).sort((a, b) => b.component_count - a.component_count));
      setMetadata(loadedData.metadata || null);
      setFeatures(loadedData.features || []);
      setDimensions(loadedData.dimensions || []);
      setTotalPages(loadedData.total_pages || 1);
      
      showToast('success', `Loaded extraction with ${loadedData.items.length} items`);
      navigate('/training');
    } catch (error: any) {
      showToast('error', `Failed to load extraction: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const toggleFolderExpand = (folderId: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // Render a file card
  const renderFileCard = (file: any, folderId?: string) => (
    <Card key={file.filename} variant="outlined" sx={{ mb: 1 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, cursor: 'pointer' }} onClick={() => handleLoadFile(file, 'pdf')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PdfIcon color="error" fontSize="small" />
              <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: '300px' }}>
                {file.original_name || file.filename}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {formatDate(file.uploaded_at)}
            </Typography>
            
            {file.extractions && file.extractions.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {file.extractions.slice(0, 2).map((extraction: any) => (
                  <Chip
                    key={extraction.job_id}
                    icon={<LinkIcon />}
                    label={`${extraction.extraction_count} items`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLoadExtraction(extraction.job_id, file.filename);
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Move to folder">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveFileDialog({ open: true, filename: file.filename, currentFolderId: folderId, jobId: file.job_id });
                  setTargetFolderId(folderId || '');
                }}
              >
                <MoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteDialog({ open: true, filename: file.filename, type: 'pdf' });
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            📁 Files
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View uploaded files and organize by customer
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<CreateFolderIcon />}
            onClick={() => setCreateFolderDialog(true)}
            size="small"
          >
            New Customer
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { loadFileList(); loadFolders(); }}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* File Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={fileTab}
          onChange={(_, newValue) => setFileTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<FolderIcon />}
            iconPosition="start"
            label={`By Customer (${folders.length})`}
            value="folders"
          />
          <Tab
            icon={<PdfIcon />}
            iconPosition="start"
            label="All PDFs"
            value="pdf"
          />
          <Tab
            icon={<ExcelIcon />}
            iconPosition="start"
            label="Excel"
            value="excel"
          />
        </Tabs>

        {/* Search Bar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'center' }}>
          <SearchIcon color="action" />
          <TextField
            size="small"
            placeholder="Find file by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, maxWidth: 400 }}
            InputProps={{
              endAdornment: searchTerm ? (
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
          {searchTerm && (
            <Typography variant="body2" color="text.secondary">
              Searching for "{searchTerm}"
            </Typography>
          )}
        </Box>

        {/* By Customer Tab */}
        {fileTab === 'folders' && (
          <Box sx={{ p: 2 }}>
            {/* Customer Folders */}
            {folders.map((folder) => (
              <Accordion 
                key={folder.id}
                expanded={expandedFolders.has(folder.id)}
                onChange={() => toggleFolderExpand(folder.id)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {expandedFolders.has(folder.id) ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />}
                    <Typography fontWeight="bold">{folder.customer_name}</Typography>
                    <Chip label={`${folder.file_count} files`} size="small" sx={{ ml: 1 }} />
                    <Chip 
                      icon={<RuleIcon />} 
                      label={`${folder.rule_count} rules`} 
                      size="small" 
                      color={folder.rule_count > 0 ? 'primary' : 'default'}
                      variant="outlined"
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteDialog({ 
                          open: true, 
                          filename: folder.customer_name, 
                          type: 'folder',
                          folderId: folder.id 
                        });
                      }}
                      disabled={folder.rule_count > 0}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {folder.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {folder.description}
                    </Typography>
                  )}
                  {/* Filter files belonging to this folder */}
                  {(() => {
                    const folderFiles = (fileList?.pdf_files.filter((f: any) => f.folder_id === folder.id) || [])
                      .filter((f: any) => !searchTerm || 
                        (f.original_name || f.filename || '').toLowerCase().includes(searchTerm.toLowerCase()));
                    if (folderFiles.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {searchTerm ? `No files matching "${searchTerm}"` : 'No files in this folder. Use the move button from "All PDFs" tab to assign files.'}
                        </Typography>
                      );
                    }
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {folderFiles.map((file: any) => (
                          <Card key={file.filename} sx={{ width: 200 }}>
                            <CardActionArea onClick={() => handleLoadFile(file, 'pdf')}>
                              <CardContent sx={{ py: 1, px: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PdfIcon color="error" fontSize="small" />
                                  <Tooltip title={file.original_name || file.filename}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {file.original_name || file.filename}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                                {file.extractions?.length > 0 && (
                                  <Chip 
                                    label={`${file.extractions.length} extraction${file.extractions.length > 1 ? 's' : ''}`}
                                    size="small"
                                    color="success"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </CardContent>
                            </CardActionArea>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
                              <Tooltip title="Move to folder">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMoveFileDialog({ open: true, filename: file.filename, currentFolderId: folder.id, jobId: file.job_id });
                                    setTargetFolderId(folder.id);
                                  }}
                                >
                                  <MoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDialog({ open: true, filename: file.filename, type: 'pdf' });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    );
                  })()}
                </AccordionDetails>
              </Accordion>
            ))}

            {/* Unassigned Files */}
            {(() => {
              const allUnassigned = fileList?.pdf_files.filter((f: any) => !f.folder_id) || [];
              const unassignedFiles = allUnassigned.filter((f: any) => !searchTerm || 
                (f.original_name || f.filename || '').toLowerCase().includes(searchTerm.toLowerCase()));
              return (
                <Accordion
                  expanded={expandedFolders.has('unassigned')}
                  onChange={() => toggleFolderExpand('unassigned')}
                  sx={{ mb: 1, bgcolor: 'background.default' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon color="disabled" />
                      <Typography fontWeight="bold" color="text.secondary">Unassigned Files</Typography>
                      <Chip 
                        label={searchTerm ? `${unassignedFiles.length}/${allUnassigned.length} files` : `${unassignedFiles.length} files`} 
                        size="small" 
                        sx={{ ml: 1 }} 
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {unassignedFiles.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {searchTerm ? `No files matching "${searchTerm}"` : 'No unassigned files'}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {unassignedFiles.map((file: any) => (
                          <Card key={file.filename} sx={{ width: 200 }}>
                            <CardActionArea onClick={() => handleLoadFile(file, 'pdf')}>
                              <CardContent sx={{ py: 1, px: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <PdfIcon color="error" fontSize="small" />
                                  <Tooltip title={file.original_name || file.filename}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                      {file.original_name || file.filename}
                                    </Typography>
                                  </Tooltip>
                                </Box>
                                {file.extractions?.length > 0 && (
                                  <Chip 
                                    label={`${file.extractions.length} extraction${file.extractions.length > 1 ? 's' : ''}`}
                                    size="small"
                                    color="success"
                                    sx={{ mt: 1 }}
                                  />
                                )}
                              </CardContent>
                            </CardActionArea>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pb: 0.5 }}>
                              <Tooltip title="Move to folder">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMoveFileDialog({ open: true, filename: file.filename, currentFolderId: undefined, jobId: file.job_id });
                                    setTargetFolderId('');
                                  }}
                                >
                                  <MoveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDialog({ open: true, filename: file.filename, type: 'pdf' });
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })()}

            {folders.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CreateFolderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No customer folders yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create folders to organize files by customer and apply customer-specific rules
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CreateFolderIcon />}
                  onClick={() => setCreateFolderDialog(true)}
                >
                  Create First Customer Folder
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* All PDFs Tab */}
        {fileTab === 'pdf' && (
          <Box sx={{ p: 3 }}>
            {!fileList || fileList.pdf_files.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No PDF files uploaded yet
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {fileList.pdf_files
                  .filter(file => !searchTerm || 
                    (file.original_name || file.filename || '').toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((file) => renderFileCard(file))}
                {searchTerm && fileList.pdf_files.filter(file => 
                    (file.original_name || file.filename || '').toLowerCase().includes(searchTerm.toLowerCase())
                  ).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No files matching "{searchTerm}"
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Excel Files Tab */}
        {fileTab === 'excel' && (
          <Box sx={{ p: 3 }}>
            {!fileList || fileList.xls_files.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No Excel files uploaded yet
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {fileList.xls_files.map((file) => (
                  <Card key={file.filename} variant="outlined">
                    <CardActionArea onClick={() => handleLoadFile(file, 'xls')}>
                      <CardContent sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <ExcelIcon color="success" fontSize="small" />
                              <Typography variant="body2" fontWeight="bold">
                                {file.original_name || file.filename}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(file.uploaded_at)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteDialog({ open: true, filename: file.filename, type: 'xls' });
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Customer Folder</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Create a folder to organize files for a specific customer. 
            You can also create customer-specific extraction rules.
          </DialogContentText>
          <TextField
            autoFocus
            label="Customer Name"
            fullWidth
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="e.g., Rapiscan Systems"
          />
          <TextField
            label="Description (optional)"
            fullWidth
            multiline
            rows={2}
            value={newFolderDescription}
            onChange={(e) => setNewFolderDescription(e.target.value)}
            placeholder="Notes about this customer or their document formats..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained" disabled={!newFolderName.trim()}>
            Create Folder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move File Dialog */}
      <Dialog open={moveFileDialog.open} onClose={() => setMoveFileDialog({ open: false, filename: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Move File to Customer Folder</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a customer folder for: <strong>{moveFileDialog.filename}</strong>
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel>Customer Folder</InputLabel>
            <Select
              value={targetFolderId}
              onChange={(e) => setTargetFolderId(e.target.value)}
              label="Customer Folder"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {folders.map((folder) => (
                <MenuItem key={folder.id} value={folder.id}>
                  {folder.customer_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveFileDialog({ open: false, filename: '' })}>Cancel</Button>
          <Button onClick={handleMoveFile} variant="contained">
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, filename: '', type: 'pdf' })}>
        <DialogTitle>
          {deleteDialog.type === 'folder' ? 'Delete Customer Folder?' : 'Delete File?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteDialog.type === 'folder' ? (
              <>
                Are you sure you want to delete the folder for <strong>{deleteDialog.filename}</strong>?
                Files in this folder will be moved to Unassigned.
              </>
            ) : (
              <>
                Are you sure you want to delete <strong>{deleteDialog.filename}</strong>?
                {deleteDialog.type === 'pdf' && ' This will also delete all associated extractions.'}
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, filename: '', type: 'pdf' })}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (deleteDialog.type === 'folder' && deleteDialog.folderId) {
                handleDeleteFolder(deleteDialog.folderId);
              } else {
                handleDeleteFile(deleteDialog.filename, deleteDialog.type as 'pdf' | 'xls');
              }
            }} 
            color="error" 
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnownView;
